import { AddOrEditMatterStatuteOfLimitationsSlideover } from "@/components/features/matter/information/AddOrEditMatterStatuteOfLimitationsSlideover";
import { MatterInsuranceContextMenu } from "@/components/features/matter/context-menus/MatterInsuranceContextMenu";
import { MatterVehicleContextMenu } from "@/components/features/matter/context-menus/MatterVehicleContextMenu";
import {
  LinkedMatterContactConnectionMembershipPopover,
  LinkedMatterMembershipPopover,
} from "@/components/features/matter/shared/LinkedMatterMembershipPopover";
import { PolicyLimitsConfirmedIcon } from "@/components/features/matter/overview/PolicyLimitsConfirmedIcon";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  CardContent,
  CurrencyWrapper,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LoadingSpinner,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@/components/ui";
import { MatterContactConnectionRichCard } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterInsurance,
  useDeleteMatterInsurance,
} from "@/lib/data/react-query";
import { useDeleteConnection } from "@/lib/data/react-query/connections/connection";
import {
  ADD_INSURANCE_TYPE_MENU_ORDER,
  formatAddInsuranceTypeMenuLabel,
  type InsuranceType,
} from "@/lib/data/static/matters/insuranceStatics";
import { getSortedMatterStatutesList } from "@/lib/data/static/matters/statuteStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Matter,
  type MatterContactConnection,
  type MatterInsurance,
  type Connection,
  type MatterStatuteOfLimitations,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  createInsuranceContactConnectionChildInput,
  getInsuranceContactConnection,
  getInsuranceContactConnections,
} from "@/lib/utils/connections/insurance-contact-connections";
import { getStatuteDefendantConnection } from "@/lib/utils/connections/statute-contact-connections";
import {
  getConnectionContactName,
  getVehicleContactConnection,
  getVehicleContactConnections,
} from "@/lib/utils/connections/vehicle-contact-connections";
import {
  formatContactsName,
  formatDate,
  formatInsuranceTitle,
  formatLocalOrIntlPhoneNumber,
  formatVehicleTitle,
} from "@/lib/utils/format";
import { getRemoveMatterContactConnectionModalCopy } from "@/lib/utils/matterContactConnectionHelpers";
import {
  compareMatterDefendantConnectionsDisplayOrder,
  getlabel,
  getMatterContactConnections,
  isDefendantPartyConnectionLabel,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";

import { ServerErrorFallbackCard } from "@/components/ui/ServerErrorFallback/ServerErrorFallbackCard";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useMatterInsuranceVehiclePermissions } from "@/hooks/useMatterInsuranceVehiclePermissions";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import {
  defendantContactIdContextForParty,
  getPartyInsurances,
  getPartyVehicles,
  getUnassignedMatterInsurances,
  getUnassignedMatterVehicles,
  type MatterPartySlideoverRole,
  type MatterPartyVehicleSlideoverRole,
} from "@/lib/utils/matterPartyCoverage";
import { ErrorBoundary } from "@sentry/react";
import {
  CarFront,
  ChevronDown,
  ChevronRight,
  Copy,
  EllipsisVertical,
  Mail,
  Pencil,
  Phone,
  PlusCircle,
  Scale,
  Shield,
  Trash2,
  UserIcon,
} from "lucide-react";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

interface MatterInformationDefendantSectionProps {
  matter: Matter;
  searchStatuteOfLimitationsId?: string;
}

const INSURANCE_TYPE_ORDER = ["Auto", "Health"];

function insuranceLiabilityTableTextClass(liability?: string | null): string {
  switch (liability) {
    case "Accepted":
      return "text-emerald-600";
    case "Denied":
      return "text-red-600";
    case "Pending":
      return "text-amber-600";
    default:
      return liability ? "text-foreground" : "text-muted-foreground";
  }
}

function insuranceCoverageTableTextClass(coverage?: string | null): string {
  switch (coverage) {
    case "Confirmed":
      return "text-emerald-600";
    case "Denied":
      return "text-red-600";
    case "Pending":
      return "text-amber-600";
    default:
      return coverage ? "text-foreground" : "text-muted-foreground";
  }
}

const formatBooleanDisplay = (value?: boolean | null) => {
  return value ? "Yes" : "No";
};

const getDefendantStatutes = (
  matter: Matter,
  defendant: MatterContactConnection,
) => {
  return getSortedMatterStatutesList(
    matter.statutesOfLimitations.filter(
      (statute) =>
        getStatuteDefendantConnection(statute)?.targetObjectId ===
        defendant.contactId,
    ),
  );
};

const getInsuranceStatusLabel = (insurance: MatterInsurance) => {
  if (insurance.coverageEnabled === false) {
    return "No Coverage";
  }

  return insurance.coverage || "-";
};

const PolicyLimitAmountPair = ({
  perPerson,
  perIncident,
}: {
  perPerson?: number | null;
  perIncident?: number | null;
}) => {
  return (
    <span className="inline-flex items-center gap-1 font-semibold">
      <CurrencyWrapper value={perPerson ?? undefined} as="span" />
      <span>/</span>
      <CurrencyWrapper value={perIncident ?? undefined} as="span" />
    </span>
  );
};

const PolicyLimitLabeledRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Text variant="subHeading" color="muted">
        {label}:
      </Text>
      {children}
    </div>
  );
};

const InsurancePolicyLimitsCell = ({
  insurance,
  partyRole,
}: {
  insurance: MatterInsurance;
  partyRole: MatterPartySlideoverRole;
}) => {
  if (partyRole === "Client") {
    return (
      <div className="flex flex-col gap-1">
        <PolicyLimitLabeledRow label="MedPay">
          <span className="font-semibold">
            <CurrencyWrapper
              value={insurance.medPayLimit ?? undefined}
              as="span"
            />
          </span>
        </PolicyLimitLabeledRow>
        <PolicyLimitLabeledRow label="UM">
          <PolicyLimitAmountPair
            perPerson={insurance.umPerPerson}
            perIncident={insurance.umPerIncident}
          />
        </PolicyLimitLabeledRow>
        <PolicyLimitLabeledRow label="UIM">
          <PolicyLimitAmountPair
            perPerson={insurance.uimPerPerson}
            perIncident={insurance.uimPerIncident}
          />
        </PolicyLimitLabeledRow>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <PolicyLimitLabeledRow label="Bodily injury">
        <PolicyLimitAmountPair
          perPerson={insurance.policyLimitPerPerson}
          perIncident={insurance.policyLimitPerIncident}
        />
        <PolicyLimitsConfirmedIcon
          policyLimitsConfirmed={insurance.policyLimitConfirmation}
        />
      </PolicyLimitLabeledRow>
    </div>
  );
};

const InsuranceExpandedField = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => {
  return (
    <div className="space-y-1">
      <Text variant="subHeading" color="muted">
        {label}
      </Text>
      <Text variant="body" className="font-semibold">
        {value ?? "-"}
      </Text>
    </div>
  );
};

function insuranceExpandedConnectionName(connection?: Connection): string {
  if (!connection) {
    return "-";
  }
  const name = (
    formatContactsName(connection.contact ?? undefined) ?? ""
  ).trim();
  return name || "-";
}

function vehicleConnectionsNamesCsv(
  connections: Matter["vehicles"][number]["connections"],
): string {
  if (connections.length === 0) {
    return "-";
  }
  const names = connections
    .map((connection) => getConnectionContactName(connection.contact))
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : "-";
}

const InsuranceContactRow = ({
  label,
  connection,
}: {
  label: string;
  connection?: Connection;
}) => {
  if (!connection) {
    return null;
  }

  const primaryPhone = connection.contact?.primaryPhone;
  const phoneNumber = primaryPhone?.number;
  const primaryEmail = connection.contact?.primaryEmail;
  const emailAddress = primaryEmail?.address;
  const formattedPhone = primaryPhone
    ? formatLocalOrIntlPhoneNumber(primaryPhone)
    : "";

  return (
    <div className="space-y-1.5">
      <div className="space-y-1">
        <Text variant="subHeading" color="muted">
          {label}
        </Text>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Text variant="body" className="font-semibold">
          {formatContactsName(connection.contact ?? undefined)}
        </Text>
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Phone className="h-4 w-4 shrink-0" />
          <Text variant="subHeading">{formattedPhone || "-"}</Text>
          {phoneNumber ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground h-7 w-7 shrink-0"
              onClick={() => {
                void copyToClipboard(phoneNumber, "Phone number");
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Mail className="h-4 w-4 shrink-0" />
          <Text variant="subHeading" className="break-all">
            {emailAddress || "-"}
          </Text>
          {emailAddress ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-muted-foreground h-7 w-7 shrink-0"
              onClick={() => {
                void copyToClipboard(emailAddress, "Email address");
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const DefendantInsuranceExpandedRow = ({
  insurance,
}: {
  insurance: MatterInsurance;
}) => {
  const insuredConnections = getInsuranceContactConnections(
    insurance,
    "Insured",
  );
  const policyHolder = getInsuranceContactConnection(
    insurance,
    "Policy Holder",
  );
  const insurer = getInsuranceContactConnection(insurance, "Insurer");
  const bodilyInjuryAdjuster = getInsuranceContactConnection(
    insurance,
    "Bodily Injury Adjuster",
  );
  const propertyDamageAdjuster = getInsuranceContactConnection(
    insurance,
    "Property Damage Adjuster",
  );
  const medPayAdjuster = getInsuranceContactConnection(
    insurance,
    "MedPay Adjuster",
  );
  const adjusterSupervisor = getInsuranceContactConnection(
    insurance,
    "Adjuster Supervisor",
  );
  const subrogationVendor = getInsuranceContactConnection(
    insurance,
    "Subrogation Vendor",
  );
  const subrogationRepresentative = getInsuranceContactConnection(
    insurance,
    "Subrogation Representative",
  );
  const isHealthInsurance = insurance.type === "Health";
  const isAutoInsurance = insurance.type === "Auto";
  const insuranceContacts = isHealthInsurance
    ? [
        { label: "Subrogation company", connection: subrogationVendor },
        {
          label: "Subrogation agent",
          connection: subrogationRepresentative,
        },
      ]
    : [
        { label: "Bodily injury adjuster", connection: bodilyInjuryAdjuster },
        {
          label: "Property damage adjuster",
          connection: propertyDamageAdjuster,
        },
        { label: "MedPay Adjuster", connection: medPayAdjuster },
        { label: "Adjuster supervisor", connection: adjusterSupervisor },
      ];

  return (
    <div className="px-6 py-5">
      {isHealthInsurance ? (
        <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
          <InsuranceExpandedField
            label="Group #"
            value={insurance.groupNumber ?? insurance.groupNumber ?? "-"}
          />
          <InsuranceExpandedField
            label="Member #"
            value={insurance.memberNumber ?? insurance.memberNumber ?? "-"}
          />
        </div>
      ) : (
        <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
          <InsuranceExpandedField
            label="Insured"
            value={
              insuredConnections
                .map(insuranceExpandedConnectionName)
                .join(", ") || "-"
            }
          />
          <InsuranceExpandedField
            label="Policy holder"
            value={insuranceExpandedConnectionName(policyHolder)}
          />
          <InsuranceExpandedField
            label="Carrier"
            value={insuranceExpandedConnectionName(insurer)}
          />
          {isAutoInsurance && (
            <InsuranceExpandedField
              label="Coverage status"
              value={getInsuranceStatusLabel(insurance)}
            />
          )}
          <InsuranceExpandedField
            label="Policy #"
            value={insurance.policyNumber ?? insurance.policyNumber ?? "-"}
          />
          <InsuranceExpandedField
            label="Claim #"
            value={insurance.claimNumber ?? insurance.claimNumber ?? "-"}
          />
          {!isAutoInsurance && (
            <InsuranceExpandedField
              label="Coverage status"
              value={getInsuranceStatusLabel(insurance)}
            />
          )}
        </div>
      )}

      {insuranceContacts.some(({ connection }) => connection) && (
        <>
          <Separator className="my-5" />
          <div className="space-y-5">
            {insuranceContacts.map(({ label, connection }) => (
              <InsuranceContactRow
                key={label}
                label={label}
                connection={connection}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const DefendantVehicleExpandedRow = ({
  matter,
  vehicle,
}: {
  matter: Matter;
  vehicle: Matter["vehicles"][number];
}) => {
  const registeredOwner = getVehicleContactConnection(
    vehicle,
    "Registered Owner",
  );
  const driver = getVehicleContactConnection(vehicle, "Driver");
  const autoShops = getVehicleContactConnections(vehicle, "Auto Shop");
  const passengers = getVehicleContactConnections(vehicle, "Passenger");
  const pedestrians = getVehicleContactConnections(vehicle, "Pedestrian");
  const insuranceTitle = formatInsuranceTitle({
    insurance: matter.insurances.find(
      (insurance) => insurance.id === vehicle.insuranceId,
    ),
  });
  const mileage =
    vehicle.mileage != null ? vehicle.mileage.toLocaleString() : "-";

  const currencyOrDash = (cents: number | null | undefined) =>
    cents != null ? <CurrencyWrapper value={cents} as="span" /> : "-";

  return (
    <div className="px-6 py-5">
      <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
        <InsuranceExpandedField
          label="Registered owner"
          value={
            registeredOwner
              ? getConnectionContactName(registeredOwner.contact)
              : "-"
          }
        />
        <InsuranceExpandedField
          label="Driver"
          value={driver ? getConnectionContactName(driver.contact) : "-"}
        />
        <InsuranceExpandedField
          label="Year"
          value={vehicle.year?.toString() || "-"}
        />
        <InsuranceExpandedField label="Color" value={vehicle.color || "-"} />
        <InsuranceExpandedField label="VIN" value={vehicle.vin || "-"} />
        <InsuranceExpandedField
          label="License plate"
          value={vehicle.licensePlateNumber || "-"}
        />
        <InsuranceExpandedField
          label="Registration state"
          value={vehicle.registrationState || "-"}
        />
        <InsuranceExpandedField label="Mileage" value={mileage} />
        <InsuranceExpandedField
          label="Insurance"
          value={insuranceTitle?.title || "-"}
        />
        <InsuranceExpandedField
          label="Auto shops"
          value={vehicleConnectionsNamesCsv(autoShops)}
        />
      </div>

      <Separator className="my-5" />

      <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
        <InsuranceExpandedField
          label="Damage severity"
          value={vehicle.damageSeverity || "-"}
        />
        <InsuranceExpandedField
          label="Repair estimate"
          value={currencyOrDash(vehicle.repairEstimate)}
        />
        <InsuranceExpandedField
          label="Total loss"
          value={currencyOrDash(vehicle.totalLoss)}
        />
        <InsuranceExpandedField
          label="Description"
          value={vehicle.description || "-"}
        />
        <InsuranceExpandedField
          label="Passengers"
          value={vehicleConnectionsNamesCsv(passengers)}
        />
        <InsuranceExpandedField
          label="Pedestrians"
          value={vehicleConnectionsNamesCsv(pedestrians)}
        />
      </div>

      <Separator className="my-5" />

      <div className="grid gap-x-10 gap-y-5 md:grid-cols-2">
        <InsuranceExpandedField
          label="Deductible paid amount"
          value={currencyOrDash(vehicle.deductiblePaidAmount)}
        />
        <InsuranceExpandedField
          label="Deductible reimbursed"
          value={formatBooleanDisplay(vehicle.deductibleReimbursed)}
        />
        <InsuranceExpandedField
          label="Loss of use"
          value={formatBooleanDisplay(vehicle.lossOfUse)}
        />
        <InsuranceExpandedField
          label="Loss of use reimbursed"
          value={formatBooleanDisplay(vehicle.lossOfUseReimbursed)}
        />
        <InsuranceExpandedField
          label="Loss of use amount"
          value={currencyOrDash(vehicle.lossOfUseAmount)}
        />
        <div className="hidden md:block" aria-hidden />
        <div className="md:col-span-2">
          <InsuranceExpandedField
            label="Loss of use details"
            value={vehicle.lossOfUseDetails || "-"}
          />
        </div>
      </div>
    </div>
  );
};

const DefendantStatutesSection = ({
  matter,
  defendant,
  searchStatuteOfLimitationsId,
}: {
  matter: Matter;
  defendant: MatterContactConnection;
  searchStatuteOfLimitationsId?: string;
}) => {
  const { hasActionPermission } = useUser();
  const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);
  const [selectedStatuteId, setSelectedStatuteId] = useState<string>();
  const statutes = useMemo(
    () => getDefendantStatutes(matter, defendant),
    [matter, defendant],
  );
  const hasViewPermission = Boolean(true);
  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.create,
  );
  const hasUpdatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatuteOfLimitations,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canOpenStatute = hasUpdatePermission || hasDeletePermission;

  useEffect(() => {
    if (
      !searchStatuteOfLimitationsId ||
      !statutes.some((statute) => statute.id === searchStatuteOfLimitationsId)
    ) {
      return;
    }
    setSelectedStatuteId(searchStatuteOfLimitationsId);
    setIsSlideoverOpen(true);
  }, [searchStatuteOfLimitationsId, statutes]);

  const openCreate = () => {
    setSelectedStatuteId(undefined);
    setIsSlideoverOpen(true);
  };

  const openEdit = (statute: MatterStatuteOfLimitations) => {
    setSelectedStatuteId(statute.id);
    setIsSlideoverOpen(true);
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Text
            variant="body"
            className="flex items-center gap-2 font-semibold"
          >
            <Scale className="h-4 w-4" />
            Statutes of limitations ({statutes.length})
          </Text>
          {hasCreatePermission && (
            <Button
              size="sm"
              variant="outline"
              className="bg-background"
              onClick={openCreate}
            >
              <PlusCircle className="h-4 w-4" />
              Add statute
            </Button>
          )}
        </div>

        {!hasViewPermission ? (
          <div className="rounded-2xl border border-dashed p-5">
            <Text variant="subHeading" color="muted">
              You do not have permission to view statutes of limitations.
            </Text>
          </div>
        ) : statutes.length === 0 ? (
          <div className="rounded-2xl border border-dashed p-5">
            <Text variant="subHeading" color="muted">
              No statutes of limitations are linked to this defendant yet.
            </Text>
          </div>
        ) : (
          <div className="space-y-3">
            {statutes.map((statute) => (
              <Card key={statute.id} className="rounded-2xl border">
                <CardContent className="flex items-start justify-between gap-4 p-5">
                  <div className="min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <Text variant="body" className="font-semibold">
                        {formatDate(statute.date) || "-"}
                      </Text>
                      <Badge
                        variant={statute.confirmed ? "success" : "warning"}
                      >
                        {statute.confirmed
                          ? "Confirmed"
                          : "Awaiting confirmation"}
                      </Badge>
                    </div>
                    {statute.description?.trim() ? (
                      <Text
                        variant="subHeading"
                        color="muted"
                        className="leading-6"
                      >
                        {statute.description}
                      </Text>
                    ) : null}
                  </div>
                  {canOpenStatute && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => {
                        openEdit(statute);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AddOrEditMatterStatuteOfLimitationsSlideover
        open={isSlideoverOpen}
        setOpen={setIsSlideoverOpen}
        matter={matter}
        statuteOfLimitationsId={selectedStatuteId}
        defaultDefendantContactId={defendant.contactId}
      />
    </>
  );
};

export const MatterPartyInsuranceSection = ({
  matter,
  partyConnection,
  partyRole,
  canViewInsurance,
  canCreateInsurance,
  canEditInsurance,
  includeUnassigned = false,
}: {
  matter: Matter;
  partyConnection: MatterContactConnection;
  partyRole: MatterPartySlideoverRole;
  canViewInsurance: boolean;
  canCreateInsurance: boolean;
  canEditInsurance: boolean;
  includeUnassigned?: boolean;
}) => {
  const { hasActionPermission } = useUser();
  const { openInsuranceModal } = useGlobalModal();
  const createInsurance = useCreateMatterInsurance({ showToast: false });
  const deleteInsurance = useDeleteMatterInsurance({ showToast: false });
  const [expandedInsuranceIds, setExpandedInsuranceIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [markingNoInsuranceType, setMarkingNoInsuranceType] =
    useState<InsuranceType | null>(null);
  const [pendingDeleteNoInsurance, setPendingDeleteNoInsurance] =
    useState<MatterInsurance | null>(null);
  const [noInsuranceSubmenuOpen, setNoInsuranceSubmenuOpen] = useState(false);
  const noInsuranceCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const clearNoInsuranceCloseTimer = () => {
    if (noInsuranceCloseTimerRef.current != null) {
      clearTimeout(noInsuranceCloseTimerRef.current);
      noInsuranceCloseTimerRef.current = null;
    }
  };
  const scheduleCloseNoInsuranceSubmenu = () => {
    clearNoInsuranceCloseTimer();
    noInsuranceCloseTimerRef.current = setTimeout(() => {
      setNoInsuranceSubmenuOpen(false);
      noInsuranceCloseTimerRef.current = null;
    }, 200);
  };

  /** Drives grid-template-rows 0fr→1fr after paint so the transition is visible (ResizeObserver + pixel height often skips the 0 state in one frame). */
  const [noInsuranceNestedExpanded, setNoInsuranceNestedExpanded] =
    useState(false);
  /** Large cap only for dev HMR: if an old chunk still reads this name, avoid ReferenceError; layout uses grid, not this value. */
  const noInsuranceNestedHeightPx = noInsuranceNestedExpanded ? 10_000 : 0;

  useLayoutEffect(() => {
    if (!noInsuranceSubmenuOpen) {
      setNoInsuranceNestedExpanded(false);
      return;
    }
    setNoInsuranceNestedExpanded(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setNoInsuranceNestedExpanded(true);
      });
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [noInsuranceSubmenuOpen]);
  const unassignedInsurances = useMemo(
    () => getUnassignedMatterInsurances(matter),
    [matter],
  );
  const unassignedInsuranceIds = useMemo(
    () => new Set(unassignedInsurances.map((insurance) => insurance.id)),
    [unassignedInsurances],
  );
  const insurances = useMemo(() => {
    const partyInsurances = getPartyInsurances(matter, partyConnection);
    return includeUnassigned
      ? [...partyInsurances, ...unassignedInsurances]
      : partyInsurances;
  }, [includeUnassigned, matter, partyConnection, unassignedInsurances]);
  const noInsuranceTypes = useMemo(
    () =>
      new Set(
        insurances
          .filter((insurance) => insurance.coverageEnabled === false)
          .map((insurance) => insurance.type)
          .filter(Boolean) as InsuranceType[],
      ),
    [insurances],
  );
  const existingInsuranceTypes = useMemo(
    () =>
      new Set(
        insurances
          .map((insurance) => insurance.type)
          .filter(Boolean) as InsuranceType[],
      ),
    [insurances],
  );

  const partyNoun =
    partyRole === "Defendant"
      ? "defendant"
      : partyRole === "OtherParty"
        ? "other party"
        : "client";
  const enforceRequiredContactForModal: "Client" | "Defendant" | undefined =
    partyRole === "OtherParty" ? undefined : partyRole;
  const requiredInsurancePartyContactId =
    partyRole === "OtherParty" ? partyConnection.contactId : undefined;
  const canDeleteInsurance = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterInsurance,
    ACTION_PERMISSION_VALUES.delete,
  );

  const groupedInsurances = useMemo(() => {
    const groups = Object.entries(
      insurances.reduce<Record<string, MatterInsurance[]>>((acc, insurance) => {
        const type = insurance.type || "Insurance";
        acc[type] = [...(acc[type] ?? []), insurance];
        return acc;
      }, {}),
    );

    return groups.sort(([left], [right]) => {
      const leftIndex = INSURANCE_TYPE_ORDER.indexOf(left);
      const rightIndex = INSURANCE_TYPE_ORDER.indexOf(right);

      if (leftIndex === -1 && rightIndex === -1) {
        return left.localeCompare(right);
      }

      if (leftIndex === -1) {
        return 1;
      }

      if (rightIndex === -1) {
        return -1;
      }

      return leftIndex - rightIndex;
    });
  }, [insurances]);

  const openCreateInsuranceWithType = (insuranceType: InsuranceType) => {
    openInsuranceModal({
      matterId: matter.id,
      editing: false,
      initialTab: "details",
      enforceRequiredContact: enforceRequiredContactForModal,
      requiredInsurancePartyContactId,
      initialData: { type: insuranceType },
      insurancePartyRole: partyRole,
      sourceMatterContactConnectionId: partyConnection.id,
      ...defendantContactIdContextForParty(partyRole, partyConnection),
    });
  };

  const markNoInsuranceForType = async (insuranceType: InsuranceType) => {
    if (existingInsuranceTypes.has(insuranceType) || markingNoInsuranceType) {
      return;
    }

    setMarkingNoInsuranceType(insuranceType);
    try {
      await createInsurance.mutateAsync({
        matterId: matter.id,
        type: insuranceType,
        coverageEnabled: false,
        connections: [
          createInsuranceContactConnectionChildInput(
            partyConnection.contactId,
            "Insured",
          ),
        ],
      });

      toast.success(`${insuranceType} insurance marked as no insurance`);
    } catch {
      toast.error(`Error marking no ${insuranceType.toLowerCase()} insurance`);
    } finally {
      setMarkingNoInsuranceType(null);
    }
  };

  const deleteNoInsurance = async (insurance: MatterInsurance) => {
    try {
      await deleteInsurance.mutateAsync({
        matterId: matter.id,
        id: insurance.id,
      });
      const insuranceType = insurance.type || "insurance";
      toast.success(
        `Removed no ${insuranceType.toLowerCase()} insurance marker`,
      );
    } catch {
      const insuranceType = insurance.type || "insurance";
      toast.error(
        `Error removing no ${insuranceType.toLowerCase()} insurance marker`,
      );
    }
  };

  const addInsuranceMenu = (
    <div className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="bg-background rounded-r-none border-r-0 pr-3"
          >
            <PlusCircle className="h-4 w-4 shrink-0" />
            Add insurance
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="z-[110] max-h-[min(60vh,360px)] overflow-y-auto"
        >
          {ADD_INSURANCE_TYPE_MENU_ORDER.map((insuranceType) => (
            <DropdownMenuItem
              key={insuranceType}
              disabled={noInsuranceTypes.has(insuranceType)}
              onSelect={() => {
                openCreateInsuranceWithType(insuranceType);
              }}
            >
              {formatAddInsuranceTypeMenuLabel(insuranceType)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <span
        aria-hidden
        className="bg-border pointer-events-none relative z-[1] -mx-px h-7 w-px shrink-0"
      />
      <DropdownMenu
        onOpenChange={(open) => {
          if (!open) {
            clearNoInsuranceCloseTimer();
            setNoInsuranceSubmenuOpen(false);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="bg-background rounded-l-none px-2"
            title="No insurance options"
            disabled={Boolean(markingNoInsuranceType)}
          >
            {markingNoInsuranceType ? (
              <LoadingSpinner className="h-4 w-4" />
            ) : (
              <EllipsisVertical className="h-4 w-4" />
            )}
            <span className="sr-only">No insurance options</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="end"
          sideOffset={4}
          className="z-[110] max-h-[min(60vh,360px)] min-w-[12rem] overflow-y-auto"
        >
          {/*
            Radix DropdownMenuSubContent hardcodes side:right, so it cannot drop below.
            In-panel nested rows mimic a submenu and expand downward.
          */}
          <DropdownMenuItem
            disabled={Boolean(markingNoInsuranceType)}
            onSelect={(event) => {
              event.preventDefault();
              setNoInsuranceSubmenuOpen((open) => !open);
            }}
            onPointerEnter={() => {
              clearNoInsuranceCloseTimer();
              setNoInsuranceSubmenuOpen(true);
            }}
            onPointerLeave={scheduleCloseNoInsuranceSubmenu}
          >
            Mark no insurance
            <ChevronRight
              className={cn(
                "ml-auto transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)]",
                noInsuranceSubmenuOpen && "rotate-90",
              )}
              aria-hidden
            />
          </DropdownMenuItem>
          {noInsuranceSubmenuOpen ? (
            <div
              role="presentation"
              data-no-insurance-nested-height-px={noInsuranceNestedHeightPx}
              className={cn(
                "grid overflow-hidden transition-[grid-template-rows,opacity] duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
                noInsuranceNestedExpanded
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0",
              )}
              onMouseEnter={clearNoInsuranceCloseTimer}
              onMouseLeave={scheduleCloseNoInsuranceSubmenu}
            >
              <div className="min-h-0 overflow-hidden">
                {ADD_INSURANCE_TYPE_MENU_ORDER.map((insuranceType) => (
                  <DropdownMenuItem
                    key={`no-insurance-${insuranceType}`}
                    className="pl-8 [&[data-disabled]]:pointer-events-auto"
                    disabled={
                      existingInsuranceTypes.has(insuranceType) ||
                      Boolean(markingNoInsuranceType)
                    }
                    onSelect={() => {
                      void markNoInsuranceForType(insuranceType);
                    }}
                  >
                    {formatAddInsuranceTypeMenuLabel(insuranceType)}
                  </DropdownMenuItem>
                ))}
              </div>
            </div>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const toggleExpandedInsurance = (insuranceId: string) => {
    setExpandedInsuranceIds((current) => {
      const next = new Set(current);

      if (next.has(insuranceId)) {
        next.delete(insuranceId);
      } else {
        next.add(insuranceId);
      }

      return next;
    });
  };

  if (!canViewInsurance) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Text
            variant="body"
            className="flex items-center gap-2 font-semibold"
          >
            <Shield className="h-4 w-4" />
            Insurance
          </Text>
          {canCreateInsurance ? addInsuranceMenu : null}
        </div>
        <div className="rounded-2xl border border-dashed p-5">
          <Text variant="subHeading" color="muted">
            You do not have permission to view insurance details.
          </Text>
        </div>
      </div>
    );
  }

  if (groupedInsurances.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Text
            variant="body"
            className="flex items-center gap-2 font-semibold"
          >
            <Shield className="h-4 w-4" />
            Insurance (0)
          </Text>
          {canCreateInsurance ? addInsuranceMenu : null}
        </div>
        <div className="rounded-2xl border border-dashed p-5">
          <Text variant="subHeading" color="muted">
            No insurance linked to this {partyNoun} yet.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groupedInsurances.map(([insuranceType, insuranceItems], index) => (
        <div key={insuranceType} className="space-y-3">
          {(() => {
            const noInsuranceItems = insuranceItems.filter(
              (insurance) => insurance.coverageEnabled === false,
            );
            const coveredInsuranceItems = insuranceItems.filter(
              (insurance) => insurance.coverageEnabled !== false,
            );
            const displayCount = coveredInsuranceItems.length;

            return (
              <>
                <div className="flex items-center justify-between gap-3">
                  <Text
                    variant="body"
                    className="flex items-center gap-2 font-semibold"
                  >
                    <Shield className="h-4 w-4" />
                    {insuranceType} Insurance
                    {displayCount > 0 ? ` (${displayCount})` : ""}
                  </Text>
                  {index === 0 && canCreateInsurance ? addInsuranceMenu : null}
                </div>
                <div className="space-y-3">
                  {noInsuranceItems.map((insurance) => (
                    <div
                      key={insurance.id}
                      className="bg-card flex items-center justify-between gap-3 rounded-2xl border px-4 py-3"
                    >
                      <Text
                        variant="body"
                        className="font-semibold text-amber-700 dark:text-amber-300"
                      >
                        No {insuranceType} Insurance
                      </Text>
                      {canDeleteInsurance && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          disabled={deleteInsurance.isPending}
                          onClick={() => {
                            setPendingDeleteNoInsurance(insurance);
                          }}
                          aria-label={`Delete no ${insuranceType.toLowerCase()} insurance`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {coveredInsuranceItems.length > 0 ? (
                    <div className="overflow-hidden rounded-2xl border">
                      <Table>
                        <TableHeader className="bg-muted/35">
                          <TableRow className="hover:bg-muted/35">
                            <TableHead className="w-[32%]">Provider</TableHead>
                            {insuranceType === "Health" ? (
                              <>
                                <TableHead>Group #</TableHead>
                                <TableHead>Member #</TableHead>
                              </>
                            ) : (
                              <>
                                <TableHead>Policy limits</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Liability</TableHead>
                              </>
                            )}
                            <TableHead className="w-14" />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {coveredInsuranceItems.map((insurance) => {
                            const insuranceId = insurance.id;
                            const insurerConnection =
                              getInsuranceContactConnection(
                                insurance,
                                "Insurer",
                              );
                            const provider =
                              (insurerConnection
                                ? formatContactsName(
                                    insurerConnection.contact ?? undefined,
                                  )
                                : undefined) || "No provider";
                            const providerMetaLines =
                              insuranceType === "Health"
                                ? []
                                : [
                                    `Claim #: ${insurance.claimNumber || "-"}`,
                                    `Policy #: ${insurance.policyNumber || "-"}`,
                                  ];
                            const providerMeta = providerMetaLines.join(" · ");
                            const isExpanded =
                              expandedInsuranceIds.has(insuranceId);
                            const insuranceLabel = `${provider} - ${
                              providerMeta || insurance.type
                            }`;
                            const handleEdit = () => {
                              openInsuranceModal({
                                matterId: matter.id,
                                insuranceId,
                                editing: true,
                                initialTab: "details",
                                enforceRequiredContact:
                                  enforceRequiredContactForModal,
                                requiredInsurancePartyContactId,
                                ...defendantContactIdContextForParty(
                                  partyRole,
                                  partyConnection,
                                ),
                              });
                            };

                            return (
                              <Fragment key={insuranceId}>
                                <MatterInsuranceContextMenu
                                  insurance={insurance}
                                  insuranceLabel={insuranceLabel}
                                  matter={matter}
                                  onEdit={handleEdit}
                                  partyConnection={partyConnection}
                                  partyRole={partyRole}
                                  trigger={
                                    <TableRow
                                      className="group cursor-pointer"
                                      onClick={() => {
                                        toggleExpandedInsurance(insuranceId);
                                      }}
                                    >
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className="text-muted-foreground -ml-2 flex h-7 w-7 shrink-0 items-center justify-center"
                                            aria-hidden
                                          >
                                            {isExpanded ? (
                                              <ChevronDown className="h-4 w-4" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4" />
                                            )}
                                          </span>
                                          <div className="space-y-1">
                                            <div className="flex items-center gap-1">
                                              <Text
                                                variant="body"
                                                className="font-semibold"
                                              >
                                                {provider}
                                              </Text>
                                              <LinkedMatterMembershipPopover
                                                currentMatterId={matter.id}
                                                enabled
                                                objectId={insurance.id}
                                                objectType="insurance"
                                              />
                                            </div>
                                            {unassignedInsuranceIds.has(
                                              insuranceId,
                                            ) ? (
                                              <Badge variant="secondary">
                                                Unassigned
                                              </Badge>
                                            ) : null}
                                            {providerMetaLines.length > 0 ? (
                                              <Text
                                                variant="caption"
                                                color="muted"
                                              >
                                                {providerMetaLines.map(
                                                  (line) => (
                                                    <span
                                                      key={line}
                                                      className="block"
                                                    >
                                                      {line}
                                                    </span>
                                                  ),
                                                )}
                                              </Text>
                                            ) : null}
                                          </div>
                                        </div>
                                      </TableCell>
                                      {insuranceType === "Health" ? (
                                        <>
                                          <TableCell>
                                            {insurance.groupNumber ??
                                              insurance.groupNumber ??
                                              "-"}
                                          </TableCell>
                                          <TableCell>
                                            {insurance.memberNumber ??
                                              insurance.memberNumber ??
                                              "-"}
                                          </TableCell>
                                        </>
                                      ) : (
                                        <>
                                          <TableCell>
                                            <InsurancePolicyLimitsCell
                                              insurance={insurance}
                                              partyRole={partyRole}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Text
                                              variant="body"
                                              className={cn(
                                                "font-medium",
                                                insuranceCoverageTableTextClass(
                                                  getInsuranceStatusLabel(
                                                    insurance,
                                                  ),
                                                ),
                                              )}
                                            >
                                              {getInsuranceStatusLabel(
                                                insurance,
                                              )}
                                            </Text>
                                          </TableCell>
                                          <TableCell>
                                            <Text
                                              variant="body"
                                              className={cn(
                                                "font-medium",
                                                insuranceLiabilityTableTextClass(
                                                  insurance.liability,
                                                ),
                                              )}
                                            >
                                              {insurance.liability || "-"}
                                            </Text>
                                          </TableCell>
                                        </>
                                      )}
                                      <TableCell>
                                        {canEditInsurance && (
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className={cn(
                                              "h-8 w-8 opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                                              isExpanded && "sm:opacity-100",
                                            )}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleEdit();
                                            }}
                                          >
                                            <Pencil className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  }
                                />
                                {isExpanded && (
                                  <TableRow className="cursor-default transition-none hover:bg-transparent">
                                    <TableCell
                                      colSpan={
                                        insuranceType === "Health" ? 4 : 5
                                      }
                                      className="p-0"
                                    >
                                      <DefendantInsuranceExpandedRow
                                        insurance={insurance}
                                      />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : null}
                </div>
              </>
            );
          })()}
        </div>
      ))}
      <ActionConfirmationModal
        open={pendingDeleteNoInsurance != null}
        setOpen={(nextOpen) => {
          if (!nextOpen) {
            setPendingDeleteNoInsurance(null);
          }
        }}
        title="Delete no insurance marker"
        description={
          pendingDeleteNoInsurance
            ? `This will remove the no ${(pendingDeleteNoInsurance.type || "insurance").toLowerCase()} insurance marker for this ${partyNoun}.`
            : "This will remove the no insurance marker."
        }
        buttonText="Delete"
        isActionPending={deleteInsurance.isPending}
        afterActionFn={async () => {
          if (!pendingDeleteNoInsurance) {
            return;
          }
          await deleteNoInsurance(pendingDeleteNoInsurance);
          setPendingDeleteNoInsurance(null);
        }}
      />
    </div>
  );
};

export const MatterPartyVehiclesSection = ({
  matter,
  partyConnection,
  partyRole,
  canViewVehicles,
  canCreateVehicle,
  canEditVehicle,
  includeUnassigned = false,
}: {
  matter: Matter;
  partyConnection: MatterContactConnection;
  partyRole: MatterPartyVehicleSlideoverRole;
  canViewVehicles: boolean;
  canCreateVehicle: boolean;
  canEditVehicle: boolean;
  includeUnassigned?: boolean;
}) => {
  const { openVehicleModal } = useGlobalModal();
  const [expandedVehicleIds, setExpandedVehicleIds] = useState<Set<string>>(
    () => new Set(),
  );
  const unassignedVehicles = useMemo(
    () => getUnassignedMatterVehicles(matter),
    [matter],
  );
  const unassignedVehicleIds = useMemo(
    () => new Set(unassignedVehicles.map((vehicle) => vehicle.id)),
    [unassignedVehicles],
  );
  const vehicles = useMemo(() => {
    const partyVehicles = getPartyVehicles(matter, partyConnection);
    return includeUnassigned
      ? [...partyVehicles, ...unassignedVehicles]
      : partyVehicles;
  }, [includeUnassigned, matter, partyConnection, unassignedVehicles]);

  const partyNoun =
    partyRole === "Defendant"
      ? "defendant"
      : partyRole === "OtherParty"
        ? "party"
        : "client";

  const vehicleEditButtonTestId =
    partyRole === "Client"
      ? "edit-client-vehicle-button"
      : partyRole === "Defendant"
        ? "edit-defendant-vehicle-button"
        : "edit-other-vehicle-button";

  const addVehicleButtonTestId =
    partyRole === "OtherParty" ? "add-other-party-vehicle-button" : undefined;

  const enforceRequiredContactForModal: MatterPartySlideoverRole | undefined =
    partyRole === "OtherParty" ? undefined : partyRole;

  const requiredVehiclePartyContactId =
    partyRole === "OtherParty" ? partyConnection.contactId : undefined;

  const openCreateVehicle = () => {
    openVehicleModal({
      matterId: matter.id,
      editing: false,
      initialTab: "details",
      enforceRequiredContact: enforceRequiredContactForModal,
      requiredVehiclePartyContactId,
      vehiclePartyRole: partyRole,
      sourceMatterContactConnectionId: partyConnection.id,
      ...defendantContactIdContextForParty(partyRole, partyConnection),
    });
  };

  const toggleExpandedVehicle = (vehicleId: string) => {
    setExpandedVehicleIds((current) => {
      const next = new Set(current);

      if (next.has(vehicleId)) {
        next.delete(vehicleId);
      } else {
        next.add(vehicleId);
      }

      return next;
    });
  };

  const openExistingVehicle = (vehicle: Matter["vehicles"][number]) => {
    openVehicleModal({
      matterId: matter.id,
      vehicleId: vehicle.id,
      editing: true,
      initialTab: "details",
      enforceRequiredContact: enforceRequiredContactForModal,
      requiredVehiclePartyContactId,
      vehiclePartyRole: partyRole,
      sourceMatterContactConnectionId: partyConnection.id,
      ...defendantContactIdContextForParty(partyRole, partyConnection),
    });
  };

  if (!canViewVehicles) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <Text
            variant="body"
            className="flex items-center gap-2 font-semibold"
          >
            <CarFront className="h-4 w-4" />
            Vehicles
          </Text>
          {canCreateVehicle && (
            <Button
              size="sm"
              variant="outline"
              className="bg-background"
              onClick={openCreateVehicle}
              data-testid={addVehicleButtonTestId}
            >
              <PlusCircle className="h-4 w-4" />
              Add vehicle
            </Button>
          )}
        </div>
        <div className="rounded-2xl border border-dashed p-5">
          <Text variant="subHeading" color="muted">
            You do not have permission to view vehicle details.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Text variant="body" className="flex items-center gap-2 font-semibold">
          <CarFront className="h-4 w-4" />
          Vehicles ({vehicles.length})
        </Text>
        {canCreateVehicle && (
          <Button
            size="sm"
            variant="outline"
            className="bg-background"
            onClick={openCreateVehicle}
            data-testid={addVehicleButtonTestId}
          >
            <PlusCircle className="h-4 w-4" />
            Add vehicle
          </Button>
        )}
      </div>

      {vehicles.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-5">
          <Text variant="subHeading" color="muted">
            No vehicles linked to this {partyNoun} yet.
          </Text>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border">
          <Table>
            <TableHeader className="bg-muted/35">
              <TableRow className="hover:bg-muted/35">
                <TableHead className="w-[34%]">Vehicle</TableHead>
                <TableHead>VIN</TableHead>
                <TableHead>License plate</TableHead>
                <TableHead>Insurance</TableHead>
                <TableHead className="w-14" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehicles.map((vehicle) => {
                const insuranceTitle = formatInsuranceTitle({
                  insurance: matter.insurances.find(
                    (insurance) => insurance.id === vehicle.insuranceId,
                  ),
                });
                const isExpanded = expandedVehicleIds.has(vehicle.id);

                return (
                  <Fragment key={vehicle.id}>
                    <MatterVehicleContextMenu
                      matter={matter}
                      vehicle={vehicle}
                      partyConnection={partyConnection}
                      partyRole={partyRole}
                      vehicleParty={
                        partyRole === "Client" ? "client" : "defendant"
                      }
                      canOpen={canEditVehicle}
                      insurancePartyContactId={
                        partyRole === "OtherParty"
                          ? partyConnection.contactId
                          : undefined
                      }
                      onOpen={() => {
                        openExistingVehicle(vehicle);
                      }}
                      trigger={
                        <TableRow
                          className="group cursor-pointer"
                          onClick={() => {
                            toggleExpandedVehicle(vehicle.id);
                          }}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span
                                className="text-muted-foreground -ml-2 flex h-7 w-7 shrink-0 items-center justify-center"
                                aria-hidden
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </span>
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Text
                                    variant="body"
                                    className="font-semibold"
                                  >
                                    {formatVehicleTitle(vehicle) || "Vehicle"}
                                  </Text>
                                  <LinkedMatterMembershipPopover
                                    currentMatterId={matter.id}
                                    enabled
                                    objectId={vehicle.id}
                                    objectType="vehicle"
                                  />
                                </div>
                                {unassignedVehicleIds.has(vehicle.id) ? (
                                  <Badge variant="secondary">Unassigned</Badge>
                                ) : null}
                                <Text variant="caption" color="muted">
                                  {vehicle.color || "-"}
                                </Text>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{vehicle.vin || "-"}</TableCell>
                          <TableCell>
                            {vehicle.licensePlateNumber || "-"}
                          </TableCell>
                          <TableCell>
                            {insuranceTitle?.title ? (
                              <Badge
                                variant="outline"
                                className="bg-light-primary text-primary w-fit max-w-full truncate"
                              >
                                {insuranceTitle.title}
                              </Badge>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell>
                            {canEditVehicle && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "h-8 w-8 opacity-100 transition-opacity sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                                  isExpanded && "sm:opacity-100",
                                )}
                                data-testid={vehicleEditButtonTestId}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openExistingVehicle(vehicle);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      }
                    />
                    {isExpanded && (
                      <TableRow className="cursor-default transition-none hover:bg-transparent">
                        <TableCell colSpan={5} className="p-0">
                          <DefendantVehicleExpandedRow
                            matter={matter}
                            vehicle={vehicle}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const DefendantShowcaseCard = ({
  matter,
  defendant,
  showInsuranceSection,
  showVehicleSection,
  canViewInsurance,
  canCreateInsurance,
  canEditInsurance,
  canViewVehicles,
  canCreateVehicle,
  canEditVehicle,
  searchStatuteOfLimitationsId,
  onEdit,
  onDelete,
}: {
  matter: Matter;
  defendant: MatterContactConnection;
  showInsuranceSection: boolean;
  showVehicleSection: boolean;
  canViewInsurance: boolean;
  canCreateInsurance: boolean;
  canEditInsurance: boolean;
  canViewVehicles: boolean;
  canCreateVehicle: boolean;
  canEditVehicle: boolean;
  searchStatuteOfLimitationsId?: string;
  onEdit: () => void;
  onDelete?: (
    matterContactConnectionId: string,
    connectionRoleLabel: string,
  ) => void;
}) => {
  const defendantLabel = getlabel(defendant);

  return (
    <Card className="overflow-hidden rounded-[24px] border shadow-sm">
      <CardContent className="p-0">
        <div className="grid gap-0 xl:grid-cols-[380px_minmax(0,1fr)]">
          <div className="border-border/60 bg-card border-r p-6">
            <MatterContactConnectionRichCard
              contact={defendant.contact}
              matterContactConnection={defendant}
              matterId={matter.id}
              communicationActions="call-text"
              tone="blue"
              highlightedBadges={
                defendant.faultStatus ? [defendant.faultStatus] : undefined
              }
              titleAccessory={
                <LinkedMatterContactConnectionMembershipPopover
                  contactId={defendant.contactId}
                  currentMatterId={matter.id}
                  relatedMatterIds={matter.relatedMatterConnections.map(
                    (connection) => connection.relatedMatterId,
                  )}
                />
              }
              maxVisibleBadges={6}
              showEditButton
              onEdit={onEdit}
              onDelete={
                onDelete
                  ? (matterContactConnectionId: string) => {
                      onDelete(matterContactConnectionId, defendantLabel);
                    }
                  : undefined
              }
              className="min-h-full w-full min-w-0 border-0 p-0"
            />
          </div>

          <div className="bg-card p-6 lg:p-8">
            <div className="space-y-7">
              <DefendantStatutesSection
                matter={matter}
                defendant={defendant}
                searchStatuteOfLimitationsId={searchStatuteOfLimitationsId}
              />
              {showInsuranceSection && (
                <MatterPartyInsuranceSection
                  matter={matter}
                  partyConnection={defendant}
                  partyRole="Defendant"
                  canViewInsurance={canViewInsurance}
                  canCreateInsurance={canCreateInsurance}
                  canEditInsurance={canEditInsurance}
                />
              )}
              {showVehicleSection && (
                <MatterPartyVehiclesSection
                  matter={matter}
                  partyConnection={defendant}
                  partyRole="Defendant"
                  canViewVehicles={canViewVehicles}
                  canCreateVehicle={canCreateVehicle}
                  canEditVehicle={canEditVehicle}
                />
              )}
              {!showInsuranceSection && !showVehicleSection && (
                <div className="rounded-2xl border border-dashed p-5">
                  <Text variant="subHeading" color="muted">
                    No insurance or vehicle modules are enabled for this matter.
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MatterInformationDefendantSectionDangerous = ({
  matter,
  searchStatuteOfLimitationsId,
}: MatterInformationDefendantSectionProps) => {
  const { openMatterContactConnectionModal } = useGlobalModal();
  const defendants = useMemo(
    () =>
      getMatterContactConnections(matter)
        .filter((connection) =>
          isDefendantPartyConnectionLabel(getlabel(connection)),
        )
        .sort(compareMatterDefendantConnectionsDisplayOrder),
    [matter],
  );
  const { hasActionPermission } = useUser();
  const hasCreateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeleteMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const {
    hasCreateInsurancePermission,
    hasViewInsurancePermission,
    hasUpdateInsurancePermission,
    hasCreateVehiclePermission,
    hasViewVehiclePermission,
    hasUpdateVehiclePermission,
  } = useMatterInsuranceVehiclePermissions();

  const deleteMatterContactConnection = useDeleteConnection();

  const [deleteMatterContactConfirmation, setDeleteMatterContactConfirmation] =
    useState<{
      matterContactConnectionId: string;
      connectionRoleLabel: string;
    } | null>(null);

  const requestDeleteDefendantConnection = useCallback(
    (matterContactConnectionId: string, connectionRoleLabel: string) => {
      setDeleteMatterContactConfirmation({
        matterContactConnectionId,
        connectionRoleLabel,
      });
    },
    [],
  );

  const confirmDeleteDefendantConnection = useCallback(async () => {
    const pending = deleteMatterContactConfirmation;
    if (!pending) {
      return;
    }
    try {
      await deleteMatterContactConnection.mutateAsync({
        connectionId: pending.matterContactConnectionId,
        matterId: matter.id,
      });
    } finally {
      setDeleteMatterContactConfirmation(null);
    }
  }, [
    deleteMatterContactConfirmation,
    deleteMatterContactConnection,
    matter.id,
  ]);

  const onDeleteDefendant = hasDeleteMatterContactConnectionPermission
    ? requestDeleteDefendantConnection
    : undefined;
  const deleteMatterContactModalCopy = deleteMatterContactConfirmation
    ? getRemoveMatterContactConnectionModalCopy(
        deleteMatterContactConfirmation.connectionRoleLabel,
      )
    : null;

  const openCreateDefendant = () => {
    openMatterContactConnectionModal({
      matterId: matter.id,
      labelOptions: ["Defendant"],
      disableTime: true,
      autoOpenContactSelection: true,
      excludeContactIds: defendants.map((defendant) => defendant.contactId),
      title: "Select defendant",
      description: "Search for and select a defendant, or create a new one.",
      defaultContactSearchObject: {},
      defaultCreateContactValues: {
        type: "individual",
      },
      createPresentation: "modal",
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Text variant="title">Defendants</Text>
        </div>
        {hasCreateMatterContactConnectionPermission && (
          <Button
            size="sm"
            onClick={openCreateDefendant}
            className="md:self-start"
          >
            <PlusCircle className="h-4 w-4" />
            Add defendant
          </Button>
        )}
      </div>

      {defendants.length === 0 ? (
        <Card className="rounded-[24px] border">
          <CardContent className="flex min-h-[260px] flex-col items-center justify-center gap-4 text-center">
            <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-2xl">
              <UserIcon className="text-muted-foreground h-7 w-7" />
            </div>
            <div className="space-y-1">
              <Text variant="body" className="font-semibold">
                No defendants added yet.
              </Text>
              <Text variant="subHeading" color="muted">
                Add a defendant to view insurance and vehicle details.
              </Text>
            </div>
            {hasCreateMatterContactConnectionPermission && (
              <Button size="sm" onClick={openCreateDefendant} variant="outline">
                <PlusCircle className="h-4 w-4" />
                Add defendant
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        defendants.map((defendant) => (
          <DefendantShowcaseCard
            key={defendant.id}
            matter={matter}
            defendant={defendant}
            showInsuranceSection={
              !matter.incidentType ||
              (matter.incidentType.insuranceModuleAccess ?? false)
            }
            showVehicleSection={
              !matter.incidentType ||
              (matter.incidentType.vehicleModuleAccess ?? false)
            }
            canViewInsurance={hasViewInsurancePermission}
            canCreateInsurance={hasCreateInsurancePermission}
            canEditInsurance={hasUpdateInsurancePermission}
            canViewVehicles={hasViewVehiclePermission}
            canCreateVehicle={hasCreateVehiclePermission}
            canEditVehicle={hasUpdateVehiclePermission}
            searchStatuteOfLimitationsId={searchStatuteOfLimitationsId}
            onEdit={() => {
              openMatterContactConnectionModal({
                matterId: matter.id,
                labelOptions: ["Defendant"],
                disableTime: true,
                matterContactConnection: defendant,
                matterContactConnectionId: defendant.id,
                matterContactConnectionContactId: defendant.contactId,
                editing: true,
                editPresentation: "modal",
              });
            }}
            onDelete={onDeleteDefendant}
          />
        ))
      )}

      <ActionConfirmationModal
        open={deleteMatterContactConfirmation != null}
        setOpen={(open) => {
          if (!open && !deleteMatterContactConnection.isPending) {
            setDeleteMatterContactConfirmation(null);
          }
        }}
        title={deleteMatterContactModalCopy?.title}
        description={deleteMatterContactModalCopy?.description}
        buttonText={deleteMatterContactModalCopy?.buttonText}
        isActionPending={deleteMatterContactConnection.isPending}
        afterActionFn={() => {
          void confirmDeleteDefendantConnection();
        }}
      />
    </div>
  );
};

export function MatterInformationDefendantSection(
  props: MatterInformationDefendantSectionProps,
) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <MatterInformationDefendantSectionDangerous {...props} />
    </ErrorBoundary>
  );
}
